import type { FunctionComponent, ReactNode } from 'react';

import { ButtonGroup, NavigationButton, TooltipButton } from '../../generic/Button';
import DropdownButton from '../../generic/DropdownButton';
import { SearchInput } from '../../generic/Forms';
import { defineSlotComponent, findAllForSlot, findFirstForSlot } from '../../layouts/slots';

import styles from './Settings.module.less';

type SettingsPageTableActionsComponent = FunctionComponent & {
  TooltipButton: typeof TooltipButton;
  SearchInput: typeof SearchInput;
  NavigationButton: typeof NavigationButton;
  DropdownButton: typeof DropdownButton;
  Text: FunctionComponent;
};

/**
 * Some settings pages have a tables of items (ie: Users, Teams, Roles, etc.),
 * and often there are "actions" such as searching the table, or buttons for creating new items.
 * This component is meant to act as a wrapper for this pattern.
 * It will position a Button to the left, and give the rest of the horizontal space for the search input.
 *
 * You can place multiple buttons, but only one SearchInput.
 *
 * @example
 * <SettingsPageTableActions>
 *   <SettingsPageTableActions.Button onClick={() => {}}>Create Team</SettingsPageTableActions.Button>
 *   <SettingsPageTableActions.SearchInput value={searchValue} onChange={setSearchValue} />
 * </SettingsPageTableActions>
 */
const SettingsPageTableActions: SettingsPageTableActionsComponent = ({ children }) => {
  //  Unlikely that anyone would ever try to use both, but in that case right now the Tootlip button will always appear first.
  //  If you are reading this in the future and need to change this, update this to be more flexible/enforce ordering.
  const buttons = [
    ...findAllForSlot('TooltipButton', children),
    ...findAllForSlot('DropdownButton', children),
    ...findAllForSlot('NavigationButton', children),
  ];
  const searchInput = findFirstForSlot('SearchInput', children);
  const text = findFirstForSlot('Text', children);
  return (
    <div className={styles.settingsPageTableActions}>
      {buttons?.length > 1 ? (
        <ButtonGroup>
          {buttons}
          {text}
        </ButtonGroup>
      ) : (
        <span className={styles.settingsPageTableActionsSingleButton}>
          {buttons}
          {text}
        </span>
      )}
      {searchInput}
    </div>
  );
};

SettingsPageTableActions.TooltipButton = defineSlotComponent('TooltipButton', TooltipButton);
//  Note: we are casting here to get around a bug where forwardRef doesn't infer the correct Component type
SettingsPageTableActions.SearchInput = defineSlotComponent('SearchInput', SearchInput) as typeof SearchInput;
SettingsPageTableActions.NavigationButton = defineSlotComponent('NavigationButton', NavigationButton);
SettingsPageTableActions.DropdownButton = defineSlotComponent('DropdownButton', DropdownButton) as typeof DropdownButton;
//  Note that this is only used on the banners settings page.
SettingsPageTableActions.Text = defineSlotComponent('Text', ({ children }: { children?: ReactNode }) => (
  <span className={styles.settingsPageTableActionsText}>
    {children}
  </span>
));

export default SettingsPageTableActions;
